import _defineProperty from "/home/runner/work/experian-public-site-tng/experian-public-site-tng/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ContextManagerMethod, addTelemetryUsage, deepClone, makePublicApi, monitor, clocksNow, callMonitored, createHandlingStack, sanitize, createIdentityEncoder, displayAlreadyInitializedError, createTrackingConsentState, timeStampToClocks, CustomerContextKey, defineContextMethod, startBufferingData, isExperimentalFeatureEnabled, ExperimentalFeature, mockable, generateUUID, timeStampNow } from '@datadog/browser-core';
import { ActionType, VitalType } from '../rawRumEvent.types';
import { createCustomVitalsState } from '../domain/vital/vitalCollection';
import { callPluginsMethod } from '../domain/plugins';
import { createPreStartStrategy } from './preStartRum';
import { startRum } from './startRum';
export function makeRumPublicApi(recorderApi, profilerApi) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var trackingConsentState = createTrackingConsentState();
  var customVitalsState = createCustomVitalsState();
  var bufferedDataObservable = startBufferingData().observable;
  var strategy = createPreStartStrategy(options, trackingConsentState, customVitalsState, function (configuration, deflateWorker, initialViewOptions, telemetry, hooks) {
    var createEncoder = deflateWorker && options.createDeflateEncoder ? function (streamId) {
      return options.createDeflateEncoder(configuration, deflateWorker, streamId);
    } : createIdentityEncoder;
    var startRumResult = mockable(startRum)(configuration, recorderApi, profilerApi, initialViewOptions, createEncoder, trackingConsentState, customVitalsState, bufferedDataObservable, telemetry, hooks, options.sdkName);
    recorderApi.onRumStart(startRumResult.lifeCycle, configuration, startRumResult.session, startRumResult.viewHistory, deflateWorker, startRumResult.telemetry);
    profilerApi.onRumStart(startRumResult.lifeCycle, startRumResult.hooks, configuration, startRumResult.session, startRumResult.viewHistory, createEncoder);
    strategy = createPostStartStrategy(strategy, startRumResult);
    callPluginsMethod(configuration.plugins, 'onRumStart', {
      strategy: strategy,
      // TODO: remove this in the next major release
      addEvent: startRumResult.addEvent
    });
    return startRumResult;
  });
  var getStrategy = function getStrategy() {
    return strategy;
  };
  var startView = function startView(options) {
    var handlingStack = createHandlingStack('view');
    callMonitored(function () {
      var sanitizedOptions = typeof options === 'object' ? options : {
        name: options
      };
      strategy.startView(_objectSpread(_objectSpread({}, sanitizedOptions), {}, {
        handlingStack: handlingStack
      }));
      addTelemetryUsage({
        feature: 'start-view'
      });
    });
  };
  var rumPublicApi = makePublicApi({
    init: function init(initConfiguration) {
      var errorStack = new Error().stack;
      callMonitored(function () {
        return strategy.init(initConfiguration, rumPublicApi, errorStack);
      });
    },
    setTrackingConsent: monitor(function (trackingConsent) {
      trackingConsentState.update(trackingConsent);
      addTelemetryUsage({
        feature: 'set-tracking-consent',
        tracking_consent: trackingConsent
      });
    }),
    setViewName: monitor(function (name) {
      strategy.setViewName(name);
      addTelemetryUsage({
        feature: 'set-view-name'
      });
    }),
    setViewContext: monitor(function (context) {
      strategy.setViewContext(context);
      addTelemetryUsage({
        feature: 'set-view-context'
      });
    }),
    setViewContextProperty: monitor(function (key, value) {
      strategy.setViewContextProperty(key, value);
      addTelemetryUsage({
        feature: 'set-view-context-property'
      });
    }),
    getViewContext: monitor(function () {
      addTelemetryUsage({
        feature: 'set-view-context-property'
      });
      return strategy.getViewContext();
    }),
    getInternalContext: monitor(function (startTime) {
      return strategy.getInternalContext(startTime);
    }),
    getInitConfiguration: monitor(function () {
      return deepClone(strategy.initConfiguration);
    }),
    addAction: function addAction(name, context) {
      var handlingStack = createHandlingStack('action');
      callMonitored(function () {
        strategy.addAction({
          name: sanitize(name),
          context: sanitize(context),
          startClocks: clocksNow(),
          type: ActionType.CUSTOM,
          handlingStack: handlingStack
        });
        addTelemetryUsage({
          feature: 'add-action'
        });
      });
    },
    startAction: monitor(function (name, options) {
      // Check feature flag only after init; pre-init calls should be buffered
      if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_ACTION)) {
        return;
      }
      // addTelemetryUsage({ feature: 'start-action' })
      strategy.startAction(sanitize(name), {
        type: sanitize(options && options.type),
        context: sanitize(options && options.context),
        actionKey: options && options.actionKey
      });
    }),
    stopAction: monitor(function (name, options) {
      if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_ACTION)) {
        return;
      }
      // addTelemetryUsage({ feature: 'stop-action' })
      strategy.stopAction(sanitize(name), {
        type: sanitize(options && options.type),
        context: sanitize(options && options.context),
        actionKey: options && options.actionKey
      });
    }),
    startResource: monitor(function (url, options) {
      // Check feature flag only after init; pre-init calls should be buffered
      if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_RESOURCE)) {
        return;
      }
      // addTelemetryUsage({ feature: 'start-resource' })
      strategy.startResource(sanitize(url), {
        type: sanitize(options && options.type),
        method: sanitize(options && options.method),
        context: sanitize(options && options.context),
        resourceKey: options && options.resourceKey
      });
    }),
    stopResource: monitor(function (url, options) {
      if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_RESOURCE)) {
        return;
      }
      // addTelemetryUsage({ feature: 'stop-resource' })
      strategy.stopResource(sanitize(url), {
        type: sanitize(options && options.type),
        statusCode: options && options.statusCode,
        size: options && options.size,
        context: sanitize(options && options.context),
        resourceKey: options && options.resourceKey
      });
    }),
    addError: function addError(error, context) {
      var handlingStack = createHandlingStack('error');
      callMonitored(function () {
        strategy.addError({
          error: error,
          // Do not sanitize error here, it is needed unserialized by computeRawError()
          handlingStack: handlingStack,
          context: sanitize(context),
          startClocks: clocksNow()
        });
        addTelemetryUsage({
          feature: 'add-error'
        });
      });
    },
    addTiming: monitor(function (name, time) {
      // TODO: next major decide to drop relative time support or update its behaviour
      strategy.addTiming(sanitize(name), time);
    }),
    setViewLoadingTime: monitor(function () {
      var callTimestamp = timeStampNow();
      strategy.setLoadingTime(callTimestamp);
      addTelemetryUsage({
        feature: 'addViewLoadingTime'
      });
    }),
    setGlobalContext: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.setContext, 'set-global-context'),
    getGlobalContext: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.getContext, 'get-global-context'),
    setGlobalContextProperty: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.setContextProperty, 'set-global-context-property'),
    removeGlobalContextProperty: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.removeContextProperty, 'remove-global-context-property'),
    clearGlobalContext: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.clearContext, 'clear-global-context'),
    setUser: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.setContext, 'set-user'),
    getUser: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.getContext, 'get-user'),
    setUserProperty: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.setContextProperty, 'set-user-property'),
    removeUserProperty: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.removeContextProperty, 'remove-user-property'),
    clearUser: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.clearContext, 'clear-user'),
    setAccount: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.setContext, 'set-account'),
    getAccount: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.getContext, 'get-account'),
    setAccountProperty: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.setContextProperty, 'set-account-property'),
    removeAccountProperty: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.removeContextProperty, 'remove-account-property'),
    clearAccount: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.clearContext, 'clear-account'),
    startView: startView,
    stopSession: monitor(function () {
      strategy.stopSession();
      addTelemetryUsage({
        feature: 'stop-session'
      });
    }),
    addFeatureFlagEvaluation: monitor(function (key, value) {
      strategy.addFeatureFlagEvaluation(sanitize(key), sanitize(value));
      addTelemetryUsage({
        feature: 'add-feature-flag-evaluation'
      });
    }),
    getSessionReplayLink: monitor(function () {
      return recorderApi.getSessionReplayLink();
    }),
    startSessionReplayRecording: monitor(function (options) {
      recorderApi.start(options);
      addTelemetryUsage({
        feature: 'start-session-replay-recording',
        force: options && options.force
      });
    }),
    stopSessionReplayRecording: monitor(function () {
      return recorderApi.stop();
    }),
    addDurationVital: function addDurationVital(name, options) {
      var handlingStack = createHandlingStack('vital');
      callMonitored(function () {
        addTelemetryUsage({
          feature: 'add-duration-vital'
        });
        strategy.addDurationVital({
          id: generateUUID(),
          name: sanitize(name),
          type: VitalType.DURATION,
          startClocks: timeStampToClocks(options.startTime),
          duration: options.duration,
          context: sanitize(options && options.context),
          description: sanitize(options && options.description),
          handlingStack: handlingStack
        });
      });
    },
    startDurationVital: function startDurationVital(name, options) {
      var handlingStack = createHandlingStack('vital');
      return callMonitored(function () {
        addTelemetryUsage({
          feature: 'start-duration-vital'
        });
        return strategy.startDurationVital(sanitize(name), {
          context: sanitize(options && options.context),
          description: sanitize(options && options.description),
          handlingStack: handlingStack
        });
      });
    },
    stopDurationVital: monitor(function (nameOrRef, options) {
      addTelemetryUsage({
        feature: 'stop-duration-vital'
      });
      strategy.stopDurationVital(typeof nameOrRef === 'string' ? sanitize(nameOrRef) : nameOrRef, {
        context: sanitize(options && options.context),
        description: sanitize(options && options.description)
      });
    }),
    startFeatureOperation: function startFeatureOperation(name, options) {
      var handlingStack = createHandlingStack('vital');
      callMonitored(function () {
        addTelemetryUsage({
          feature: 'add-operation-step-vital',
          action_type: 'start'
        });
        strategy.addOperationStepVital(name, 'start', _objectSpread(_objectSpread({}, options), {}, {
          handlingStack: handlingStack
        }));
      });
    },
    succeedFeatureOperation: monitor(function (name, options) {
      addTelemetryUsage({
        feature: 'add-operation-step-vital',
        action_type: 'succeed'
      });
      strategy.addOperationStepVital(name, 'end', options);
    }),
    failFeatureOperation: monitor(function (name, failureReason, options) {
      addTelemetryUsage({
        feature: 'add-operation-step-vital',
        action_type: 'fail'
      });
      strategy.addOperationStepVital(name, 'end', options, failureReason);
    })
  });
  return rumPublicApi;
}
function createPostStartStrategy(preStartStrategy, startRumResult) {
  return _objectSpread({
    init: function init(initConfiguration) {
      displayAlreadyInitializedError('DD_RUM', initConfiguration);
    },
    initConfiguration: preStartStrategy.initConfiguration
  }, startRumResult);
}